<template>
    <div class="appointments">
        <div class="page-header">
            <div class="left">
                <h2 class="page-title">{{ $page.props.lang.appointments || "Appointments" }}</h2>
            </div>
            <div class="right">
                <v-btn color="primary" dark @click="newAppointmentModal = true">
                    <v-icon small>mdi-plus</v-icon>
                    <span v-if="$vuetify.breakpoint.mdAndUp" class="ml-2">{{ $page.props.lang.newAppointment || "New Appointment" }}</span>
                </v-btn>
            </div>
        </div>
        <div class="filter-section calendar-trainers">
            <div class="date" v-if="$refs.calendar && $vuetify.breakpoint.sm">
                <span v-show="currentType!=='day'">
                    {{ $refs.calendar.lastStart.date | formatDate }} - {{ $refs.calendar.lastEnd.date | formatDate }}
                </span>
                <span v-show="currentType=='day'">
                    {{ $refs.calendar.lastStart.date | formatDate }}
                </span>
            </div>
            <div>
            <v-container>
            <v-row style="margin:-34px; padding:25px 3px">
                <v-col cols="12" sm="12" md="6">
                    <v-select 
                        v-model="form.location_id"  :items="categories" item-text="name" item-value="id"
                        dense outlined hide-details :label="$page.props.lang.locations || 'Locations'" clearable>
                    </v-select>
                </v-col>
                <v-col cols="12" sm="12" md="6">
                    <v-select 
                        v-model="form.trainer_id" @change="trainerSelected" :items="trainers" item-text="full_name" item-value="id"
                        dense outlined hide-details :label="$page.props.lang.trainers || 'Trainers'" clearable>
                    </v-select>
                </v-col>
            </v-row>
            </v-container>
            </div>
        </div>
        <v-dialog v-model="newAppointmentModal" max-width="500px" content-class="add-appointment">
            <v-card class="event-panel">
                <div class="modal-header">
                    <h4 class="modal-headline">{{ $page.props.lang.newAppointment || "New Appointment" }}</h4>
                    <div class="tools">
                        <v-btn icon @click="newAppointmentModal = false"><v-icon>mdi-close</v-icon></v-btn>
                    </div>
                </div>
                <v-card-text>
                    <v-container>
                        <div class="fields">
                            <p class="custom-label">{{ $page.props.lang.eventType || 'Class' }}</p>
                            <v-select :items="courses" item-text="title" item-value="id"
                            @change="newAppointmentCourseSelected"
                            v-model="newAppointment.course_id" :hide-details="!responseErrors.course_id" 
                            outlined dense solo flat :error-messages="responseErrors.course_id" @focus="responseErrors.course_id=''">{{ "Title" }}
                            </v-select>
                            <div class="grid">
                                <div>
                                    <p class="custom-label">{{ $page.props.lang.startDate || 'Start Date' }}</p>
                                    <v-menu
                                    v-model="startDatePicker"
                                    :close-on-content-click="false"
                                    :return-value.sync="today" 
                                    transition="scale-transition"
                                    offset-y 
                                    min-width="290px">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field 
                                            :value="newAppointment.start_date | formatDate" :hide-details="!responseErrors.start_date"
                                            append-icon="mdi-menu-down" readonly v-bind="attrs" v-on="on" @click:append="startDatePicker = true"  
                                            :error-messages="responseErrors.start_date" @focus="responseErrors.start_date=''"
                                            outlined dense solo flat>
                                            </v-text-field>
                                        </template>
                                        <v-date-picker 
                                        v-model="newAppointment.start_date" 
                                        :first-day-of-week="1" no-title scrollable color="primary">
                                            <v-spacer></v-spacer>
                                            <v-btn color="regular" @click="startDatePicker = false">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                                            <v-btn color="primary" @click="startDatePicker = false">{{ $page.props.lang.ok || "Ok" }}</v-btn>
                                        </v-date-picker>
                                    </v-menu>
                                </div>
                                <div class="vue-timepicker-wrap">
                                    <p class="custom-label">{{ $page.props.lang.startTime || "Start time" }}</p>
                                    <vue-timepicker 
                                    v-model="newAppointment.start_time" 
                                    close-on-complete hide-disabled-items hide-clear-button fixed-dropdown-button format=""
                                    :minute-interval="15" :hour-range="[[5,23]]">
                                        <template v-slot:dropdownButton>
                                            <v-icon>mdi-menu-down</v-icon>
                                        </template>
                                    </vue-timepicker>
                                    <p v-if="responseErrors.start_time" 
                                    class="custom-errors v-bounce">
                                    {{ responseErrors.start_time[0] }}
                                    </p>
                                </div>
                            </div>
                            <p class="custom-label">{{ $page.props.lang.trainer || 'Trainer' }}</p>
                            <v-select 
                            v-model="newAppointment.trainer_id" item-value='id' item-text='full_name' 
                            :items="trainers" 
                            dense outlined :hide-details="!responseErrors.trainer_id" 
                            :error-messages="responseErrors.trainer_id" @focus="responseErrors.trainer_id=''">
                            </v-select>
                            <p class="custom-label">{{ $page.props.lang.duration || 'Duration' }}</p>
                            <v-select 
                            v-model="newAppointment.duration" :items="times" 
                            dense outlined :hide-details="!responseErrors.trainer_id" 
                            :error-messages="responseErrors.duration" @focus="responseErrors.duration=''">
                            </v-select>
                            <div class="grid" v-if="$vuetify.breakpoint.mdAndUp">
                                <div>
                                    <p class="custom-label">{{ $page.props.lang.location || 'Location' }}</p>
                                    <v-select 
                                        v-model="newAppointment.location_id" :items="categories" 
                                        dense outlined :hide-details="!responseErrors.location_id"
                                        item-text="name" item-value="id"
                                        :error-messages="responseErrors.location_id" @focus="responseErrors.location_id=''">
                                    </v-select>
                                </div>
                                <div>
                                    <v-btn @click="openManageCategoriesModal = true" text tile small class="mt-7" color="primary">{{ $page.props.lang.manageLocations || "Manage Locations" }}</v-btn>
                                </div>
                            </div>
                            <div v-else>
                                <p class="custom-label">{{ $page.props.lang.location || 'Location' }}</p>
                                    <v-select 
                                        v-model="newAppointment.location_id" :items="categories" 
                                        dense outlined :hide-details="!responseErrors.location_id"
                                        item-text="name" item-value="id"
                                        :error-messages="responseErrors.duration" @focus="responseErrors.location_id=''">
                                    </v-select>
                                <v-btn @click="openManageCategoriesModal = true" text tile x-small class="mb-4 ml-2" color="primary">{{ $page.props.lang.manageLocations || "Manage Locations" }}</v-btn>
                            </div>
                            <p class="custom-label">{{ $page.props.lang.choose || "Choose appointment color" }}</p>
                            <div class="colors">
                                <template v-for="color in colors">
                                    <div @click="picked(color)" 
                                    :class="color===pickedColor ? 'is-selected' : ''" class="color" :key="color" :style="{ background: color }">
                                    </div>
                                </template>
                                <p v-if="responseErrors.color" class="message-error" v-html="responseErrors.color[0]"></p>
                            </div>
                            <v-switch 
                            v-model="newAppointment.recurring" :label="$page.props.lang.recurring || 'Recurring'" hide-details>
                            </v-switch>
                            <div v-if="newAppointment.recurring" class="repeat-options">
                                <p class="days custom-label">
                                    {{ $page.props.lang.checkDays || "Check days" }}
                                    <span v-if="responseErrors.days" v-html="responseErrors.days[0]"></span>
                                </p>
                                <div class="checkboxes">
                                    <template v-for="day in weekDays">
                                        <v-checkbox v-model="newAppointment.days" :value="day.value" :key="day.value" hide-details dense class="day-check">
                                            <template v-slot:label>
                                                <span id="checkboxLabel"> {{ day.text }}</span>
                                            </template>
                                        </v-checkbox>
                                    </template>
                                </div>
                                <p class="custom-label">{{ $page.props.lang.endDate || 'End Date' }}</p>
                                <v-menu
                                v-model="endDatePicker"
                                :close-on-content-click="false"
                                :return-value.sync="today"
                                transition="scale-transition"
                                offset-y
                                min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field 
                                        :value="newAppointment.end_date | formatDate" append-icon="mdi-menu-down" 
                                        readonly v-bind="attrs" v-on="on" @click:append="endDatePicker = true" outlined dense
                                        :error-messages="responseErrors.end_date" @focus="responseErrors.end_date=''"
                                        :hide-details="!responseErrors.end_date">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker v-model="newAppointment.end_date" :min="today" no-title scrollable color="primary">
                                        <v-spacer></v-spacer>
                                        <v-btn color="regular" @click="endDatePicker = false">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                                        <v-btn color="primary" @click="endDatePicker = false">{{ $page.props.lang.ok || "Ok" }}</v-btn>
                                    </v-date-picker>
                                </v-menu>
                            </div>

                            <v-switch v-model="newAppointment.is_online" :label="$page.props.lang.is_online || 'Online'" hide-details></v-switch>
                            <div v-if="newAppointment.is_online">
                                <p class="custom-label">{{ $page.props.lang.enter_url || 'Link' }}</p>
                                <v-text-field v-model="newAppointment.session_url" outlined dense
                                :error-messages="responseErrors.session_url" @focus="responseErrors.session_url=''"
                                :hide-details="!responseErrors.session_url">
                                </v-text-field>
                            </div>

                            <p class="custom-label">{{ $page.props.lang.selectMembers || 'Select Members' }}</p>
                            <v-autocomplete
                            :search-input.sync="newSearchMembers"
                            :items="newResultMembers" 
                            v-model="newAppointment.members" 
                            @change="newSearchMembers = null"
                            item-text="full_name" 
                            return-object
                            :loading="isLoading" append-icon=""
                            hide-no-data
                            hide-selected
                            multiple
                            outlined dense solo flat class="search-members">
                            </v-autocomplete>
                            <ul class="selected-members">
                                <li v-for="member in newAppointment.members" :key="member.id" class="selected-member">
                                    <v-avatar size="28">
                                        <img :src="member.profile_photo + '?w=56'" alt="">
                                    </v-avatar>
                                    <p>
                                        {{ member.full_name }}
                                        <v-btn icon>
                                            <v-icon @click="removeFromNew(member)" small>mdi-close</v-icon>
                                        </v-btn>
                                    </p>
                                </li>
                            </ul>
                        </div>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="regular" @click="newAppointmentModal = false">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                            <v-btn color="primary" @click="create(false)" :loading="addEventLoading">{{ $page.props.lang.save || "Save" }}</v-btn>
                        </v-card-actions>
                    </v-container>
                </v-card-text>
            </v-card>
        </v-dialog>
        <div class="calendar-container">
            <v-toolbar flat elevation="0" class="mb-2 main-toolbar" v-show="!onMobile">
                <v-btn
                    outlined small
                    color="grey darken-2"
                    @click="setToday(currentType)"
                >{{ $page.props.lang.today || "Today" }}</v-btn>
                <v-btn icon class="ma-2" @click="prev" small>
                    <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn icon class="ma-2" @click="next" small>
                    <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
                <div class="tabs flex">
                    <template v-for="type in types">
                        <v-btn 
                        @click="view(type.value)" :key="type.value" outlined small class="custom" color="grey darken-1" 
                        :class="type.value === currentType ? 'active-view' : ''">
                        {{ type.title }}
                        </v-btn>
                    </template>
                </div>

                <v-toolbar-title v-if="$refs.calendar && !($vuetify.breakpoint.sm || $vuetify.breakpoint.xs)">
                    <span v-show="currentType!=='day'">
                        {{ $refs.calendar.lastStart.date | formatDate }} - {{ $refs.calendar.lastEnd.date | formatDate }}
                    </span>
                    <span v-show="currentType=='day'">
                        {{ $refs.calendar.lastStart.date | formatDate }}
                    </span>
                </v-toolbar-title>
            </v-toolbar>

            <v-toolbar elevation="1" class="mb-2 mobile-toolbar" v-show="onMobile">
                <v-btn icon class="ma-2" @click="mobilePrev">
                    <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <div class="mobile-week-days">
                    <div 
                    v-for="(day, index) in weekDays" :key="day.id"
                    @click="mobileDaySelect(day, index)"
                    :class="mobileSelectedDay.id===day.id ? 'active' : ''" class="tab">
                        <p class="day">{{ day.text.substr(0,3) }}</p>
                        <p class="date">{{ day.date | formatMobileDates }}</p>
                    </div>
                </div>
                <v-btn icon class="ma-2" @click="mobileNext">
                    <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
            </v-toolbar>
            <v-sheet elevation="1">
                <v-calendar
                    v-model="focus"
                    :locale="setLanguage"
                    :today="today"
                    :weekdays="weekday"
                    :events="appointmentsData"
                    :type="currentType"
                    :event-color="getEventColor" short-months
                    first-time="05:00" 
                    interval-count="19" 
                    interval-height="70px"
                    ref="calendar"
                    color="primary"
                    @click:event="showEvent"
                    @mousedown:event="startDrag"
                    @mousedown:time="startTime"
                    @mousemove:time="mouseMove"
                    @mouseup:time="endDrag"
                    @click:more="viewDay"
                    @click:date="viewDay"
                    >
                    <template v-slot:event="{ event, timed }">
                            <div class="v-event-draggable pl-1" :class="{'canceled' : event.status===0}">
                                <p class="event-title pt-1">
                                    <strong>{{ event.name }}: 
                                        <v-icon v-show="event.status===2" class="pb-1" color="#FFFFFF" small>mdi-check-circle-outline</v-icon>
                                    </strong>
                                    {{ formatEventTime(event.start) }} - {{ formatEventTime(event.end) }}
                                </p>
                                <p class="event-trainer">{{event.trainer.full_name}}</p>
                                <p class="event-booked">
                                    {{ $page.props.lang.isBooked || "Booked" }}: 
                                    {{ event.booked_seats }}/{{ event.max_seats }}
                                </p>                            
                            </div>
                            <div
                                v-if="timed"
                                class="v-event-drag-bottom"
                                @mousedown.stop="extendBottom(event)"
                            ></div>
                    </template>
                </v-calendar>
            </v-sheet>
        </div>
        <ConfirmModal 
                :title="$page.props.lang.confirmModalTitle || 'Confirmation'" 
                :description="$page.props.lang.swap_confirmation_description || 'This coach already has an appointment in this period. Do you want to continue the change?'" 
                :open="confirmAddModal" 
                @yes="create(true)" 
                @no="closeConfirmAddModal()" 
                @close="confirmAddModal = false">
        </ConfirmModal>
        <ConfirmModal 
                :title="$page.props.lang.confirmModalTitle || 'Confirmation'" 
                :description="$page.props.lang.swap_confirmation_description || 'This coach already has an appointment in this period. Do you want to continue the change?'" 
                :open="confirmModal" 
                @yes="keepChange()" 
                @no="cancelDrag()" 
                @close="confirmModal = false">
        </ConfirmModal>
        <ViewModal 
        :locations="categories"
        :open="viewAppointmentModal" 
        :trainers="trainers"
        :courses="courses"
        :selectedEvent="selectedEvent"
        :sms_credit="sms_credit"
        :use_mobile_app="use_mobile_app"
        :editAppointment="editAppointment"
        :pickedColor="pickedColor"
        :eventLoading="eventLoading"
        @close="closeViewAppointmentModal()"
        @fetchCurrent="fetchCurrent">
        </ViewModal>
         <ManageCategories 
            v-if="openManageCategoriesModal" 
            type="location" 
            :title="$page.props.lang.manageLocations || 'Manage Locations'"
            :modal="openManageCategoriesModal" 
            :categories="categories"
            @close="openManageCategoriesModal = false">
        </ManageCategories>
    </div>
</template>
<style scoped>
    #checkboxLabel {
        margin-left: -5px;
    }
</style>
<script>
import AppLayout from '@/js/Layouts/AppLayout'
import ConfirmModal from '@/js/Components/ConfirmModal'
import VueTimepicker from 'vue2-timepicker'
import 'vue2-timepicker/dist/VueTimepicker.css'
import ViewModal from '@/js/Pages/Backoffice/Calendar/Components/ViewModal'
import moment from 'moment'
import EventBus from '@/js/eventbus'
import ManageCategories from '@/js/Components/ManageCategories'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

moment.updateLocale('en', {
    week: {
        dow: 1,
    },
})
export default {
    metaInfo() {
        return {
            title:  this.$page.props.lang.pageTitle || "Calendar"
        }
    },
    layout: AppLayout,
    components: {
        ConfirmModal,
        VueTimepicker,
        ViewModal,
        ManageCategories
    },
    props: {
        filters: Object,
        categories: Array,
        trainers: Array,
        appointments: Array,
        courses: Array,
        sms_credit: Number,
        use_mobile_app: Number,
        end: Object,
        lang: Object,
        locale: String
    },
    data(){
        return {
            openManageCategoriesModal: false,
            appointmentsData: this.appointments,
            language: this.locale,
            today : moment().format('YYYY-MM-DD'),
            trainer_id: null,
            location_id: null,
            currentType: 'week',
            types: [
                {value: 'day', title: this.lang.day || 'day'},
                {value: 'week', title: this.lang.week || 'week'},
                {value: 'month', title: this.lang.month || 'month'}
            ],
            mode: 'stack',
            modes: ['stack', 'column'],
            weekday: [1, 2, 3, 4, 5, 6, 0],
            weekdays: [
                { text: 'Sun - Sat', value: [0, 1, 2, 3, 4, 5, 6] },
                { text: 'Mon - Sun', value: [1, 2, 3, 4, 5, 6, 0] },
                { text: 'Mon - Fri', value: [1, 2, 3, 4, 5] },
                { text: 'Mon, Wed, Fri', value: [1, 3, 5] },
            ],
            form: {
                from_date: this.filters.from_date,
                to_date: this.filters.to_date,
                trainer_id: this.filters.trainer_id,
                location_id: this.filters.location_id
            },
            focus: '',
            isLoading: false,
            newAppointmentModal: false,
            startDatePicker: false,
            startTimePicker: false,
            endDatePicker: false,
            newSearchMembers: null,
            newResultMembers: [],
            addEventLoading: false,
            newAppointment: {
                start_date: moment().format('YYYY-MM-DD'),
                start_time: {},
                end_date: moment().add(30, 'days').toISOString().substr(0, 10),
                recurring: false,
                days: [],
                members: [],
                is_online: false,

            },
            editAppointment: {
                start_date: moment().format('YYYY-MM-DD'),
                start_time: {hh: '', mm: ''},
                end_date: null,
                recurring: false,
                is_online: false,
                days: [],
                members: [],
                color: '',
                change_type: 'once'
            },
            resetAppointment: {
                start_date: moment().format('YYYY-MM-DD'),
                start_time: {hh: '', mm: ''},
                end_date: null,
                recurring: false,
                is_online:false,
                days: [],
                members: [],
                color: ''
            },
            times: [
                {value: 15, text: '00:15'},
                {value: 30, text: '00:30'},
                {value: 45, text: '00:45'},
                {value: 60, text: '01:00'},
                {value: 75, text: '01:15'},
                {value: 90, text: '01:30'},
                {value: 105, text: '01:45'},
                {value: 120, text: '02:00'},
                {value: 135, text: '02:15'},
                {value: 150, text: '02:30'},
                {value: 165, text: '02:45'},
                {value: 180, text: '03:00'}
            ],
            repeating: false,
            weekDays: [
                { id: 0, value:'MO', text: this.lang.monday || 'Mon'},
                { id: 1, value:'TU', text: this.lang.tuesday || 'Tue'},
                { id: 2, value:'WE', text: this.lang.wednesday || 'Wed'},
                { id: 3, value:'TH', text: this.lang.thursday || 'Thu'},
                { id: 4, value:'FR', text: this.lang.friday || 'Fri'},
                { id: 5, value:'SA', text: this.lang.saturday || 'Sat'},
                { id: 6, value:'SU', text: this.lang.sunday || 'Sun'},
            ],
            colors: [
                '#4CAF50', //green
                '#2196F3', //blue
                '#9C27B0', //purple
                '#607D8B', //blue-grey
                '#FF5722', //deep-orange
                '#CDDC39', //lime
            ],
            pickedColor: '',
            messageObjects: [],
            responseErrors: {},
            selectedEvent: {
                start: '',
                end: '',
                name: '',
                color: '',
                id: null,
                duration: null,
                trainer: {
                    full_name: ''
                },
                notes: []
            },
            confirmModal:false,
            confirmAddModal:false,
            previousAppoinmentStart: null,
            selectedElement: null,
            selectedEventOpen: false,
            preventShowEvent: false,
            eventMoved: false,
            dragEvent: null,
            dragStart: null,
            dragTime: null,
            createStart: null,
            createEvent: null,
            extendOriginal: null,
            eventLoading: true,
            viewAppointmentModal: false,
            mobileSelectedDay: {
                id: null,
                value: '',
                text: ''
            },
        }
    },
    computed: {
        setLanguage(){
            if(this.language == 'sr'){
                return this.language = 'sr-Latn';
            }else{
                return this.language;
            }
        },
        watchedDays() {
            return this.newAppointment.days
        },
        trainersData() {
            if(this.$page.props.p['calendar.all.read'] === true) {
                return this.trainers
            } else {
                return this.trainers.filter(item => item.id === this.trainer_id)
            }
        },
        onMobile: {
            get() {
                if(this.$vuetify.breakpoint.mdAndDown===true) {
                    return true
                } else {
                    return false
                }
            },
            set() {
                if(this.$vuetify.breakpoint.mdAndDown===true) {
                    return true
                } else {
                    return false
                }
            }
        },
    },

    mounted() {
        this.$refs.calendar.checkChange()
        this.$refs.calendar.move(0)

        if (this.onMobile===true) {
            this.mobileInitCalendar()
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                this.$inertia.get("/backoffice/calendar", pickBy(this.form), { preserveState: true })
            }, 250)

        },
        viewAppointmentModal(val){

            if(val === false){
                this.$inertia.get("/backoffice/calendar", pickBy(this.form), { preserveState: true,  preserveScroll: true })
            }

        },
        onMobile(value) {
            if (value===true) {
                this.mobileSetCalendar()
            }
        },
        appointments(val) {
            this.appointmentsData = val
        },
        watchedDays(val) {
            if(val) {
                this.responseErrors.days = ''
            }
        },
        newAppointmentModal(val) {
            if(val===false) {
                this.newAppointment.color = ''
                this.pickedColor = ''
                this.newAppointment = Object.assign({}, this.resetAppointment)
                this.responseErrors = {}
                this.newResultMembers = []
            }
        },
        newSearchMembers(val) {
            if (val === null) {
                this.newResultMembers = []
                return
            } 
            if(this.newResultMembers.length > 0) return
            if (this.isLoading) return

            this.isLoading = true

            axios.get('/backoffice/lookup/members', { params: { search: val }})
                .then(response => {
                    this.newResultMembers = response.data.results
                    this.isLoading = false
                })
        },
    },

    methods: {

        startDrag ({ event, timed }){

            if(event && timed){
                this.dragEvent = event;
                this.dragTime = null;
                this.extendOriginal = null;
            }
        },

        startTime(slot){
            var time = this.toTime(slot);
            var rounded = new Date(this.roundTime(time));
            
            if(this.dragEvent && this.dragTime === null){
                const start = new Date(this.dragEvent.start).getTime();
                this.dragTime = time - start
            }else{
                this.newAppointment.start_date = slot.date;
                this.newAppointment.start_time = {HH:  moment(rounded).format('HH'), mm: moment(rounded).format('mm')};
                this.newAppointmentModal = true;
            }
        },

        mouseMove(tms){
            const mouse = this.toTime(tms);
           
            if (this.dragEvent && this.dragTime !== null) {
                const start = new Date(this.dragEvent.start).getTime();
                const end = new Date(this.dragEvent.end).getTime();
                const duration = end - start;
                const newStartTime = mouse - this.dragTime;
                const newStart = this.roundTime(newStartTime);
                const newEnd = newStart + duration;

                this.eventMoved = true;
                this.dragEvent.start = moment(new Date(this.roundTime(newStart))).format('YYYY-MM-DD HH:mm:ss');
                this.dragEvent.end =  moment(new Date(this.roundTime(newEnd))).format('YYYY-MM-DD HH:mm:ss'); 

            }else if(this.extendOriginal !== null && this.createStart !== null){
                const mouseRounded = this.roundTime(mouse, false);
                const min = Math.min(mouseRounded, this.createStart);
                const max = Math.max(mouseRounded, this.createStart);

                this.dragEvent.start = moment(new Date(this.roundTime(min))).format('YYYY-MM-DD HH:mm:ss');
                this.dragEvent.end = moment(new Date(this.roundTime(max))).format('YYYY-MM-DD HH:mm:ss');
                this.eventMoved = true;
            }
        },
        endDrag (e) {

            this.dragTime = null;
        
            if(this.eventMoved){
                this.eventMoved = false;
                this.preventShowEvent = true;
                let appointment = Object.assign({}, this.dragEvent);  
                axios.post('/backoffice/calendar/appointment/dragupdate', {
                    id: appointment.id, 
                    start: appointment.start, 
                    end: appointment.end, 
                    trainer_id: appointment.trainer.id,
                    keep_change: false
                    })
                    .then(response => {
                            if(response.data.status==="CONFIRMATION"){
                                this.previousAppoinmentStart = response.data.appointment;
                                this.previousAppoinmentStart.end_date = moment(this.previousAppoinmentStart.end_date).format('YYYY-MM-DD HH:mm:ss');
                                this.confirmModal = true;                        
                                }
                            else if(response.data.status==="ERROR"){
                                EventBus.$emit('SHOW_NOTIFICATION',{
                                        message: response.data.message,
                                        color: 'error',
                                        timeout: 5000
                                    })
                            }
                        })
                        .catch(error => {
                            EventBus.$emit('SHOW_NOTIFICATION',{
                                    message: error.response.data.message,
                                    color: 'error',
                                    timeout: 10000
                                })
                        })
                
            }

            this.createStart = null;
            this.extendOriginal = null;
            
        },

        extendBottom (event) {
            this.dragEvent = event
            this.createStart = new Date(event.start).getTime();
            this.extendOriginal =  new Date(event.end).getTime();    
        },

        cancelDrag () {

            var foundIndex = this.appointmentsData.findIndex(x => x.id == this.previousAppoinmentStart.id);
           
            if(foundIndex != -1){
                this.appointmentsData[foundIndex]['start'] = this.previousAppoinmentStart.start_date; 
                this.appointmentsData[foundIndex]['end'] = this.previousAppoinmentStart.end_date; 
            }
            this.confirmModal = false;
        },

        keepChange(){
            let appointment = Object.assign({}, this.dragEvent);  
            axios.post('/backoffice/calendar/appointment/dragupdate', {
                id: appointment.id, 
                start: appointment.start, 
                end: appointment.end, 
                trainer_id: appointment.trainer.id,
                keep_change: true
                }).then(response => {
                        if(response.data.status==="OK") {
                           this.confirmModal = false;
                        }
                        else{
                            EventBus.$emit('SHOW_NOTIFICATION',{
                                    message: response.data.meassge,
                                    color: 'error',
                                    timeout: 5000
                                })
                        }
                    })
                    .catch(error => {
                        EventBus.$emit('SHOW_NOTIFICATION',{
                                message: error.response.data.message,
                                color: 'error',
                                timeout: 10000
                            })
                    })

            this.dragEvent = null;
            
        },
 
        toTime (tms) {
            return new Date(tms.year, tms.month - 1, tms.day, tms.hour, tms.minute).getTime()
        },

        roundTime (time, down = true) {
            const roundTo = 15;
            const roundDownTime = roundTo * 60 * 1000;

            return down
            ? time - time % roundDownTime
            : time + (roundDownTime - (time % roundDownTime))
        },

        // calendar on mobile
        async mobilePrev() {
            let from = await moment(this.$refs.calendar.lastStart.date).add(-7, 'days').startOf('week').format("YYYY-MM-DD")
            this.focus = from
            this.mobileSelectedDay = this.weekDays[0]
            this.fetchAppointments(from, from)
            this.weekDays.map(item => {
                return item.date = moment(item.date).add(-6, 'days').toISOString().substr(0, 10)
            })
        },
        async mobileNext() {
            let from = await moment(this.$refs.calendar.lastStart.date).add(7, 'days').startOf('week').format("YYYY-MM-DD")
            this.focus = from
            this.mobileSelectedDay = this.weekDays[0]
            this.fetchAppointments(from, from)
            this.weekDays.map(item => {
                return item.date = moment(item.date).add(8, 'days').toISOString().substr(0, 10)
            })
        },
        async mobileDaySelect(day, index) {
            await this.$refs.calendar.move(index - this.mobileSelectedDay.id)
            let from = await this.$refs.calendar.lastStart.date
            this.fetchAppointments(from, from)
            this.mobileSelectedDay = day
        },
        mobileInitCalendar() {
            
            this.onMobile = true;
            this.currentType = 'day';
            this.setToday('day');

            var currentDate = moment();

            var weekStart = currentDate.clone().startOf('isoWeek');
           
            for (var i = 0; i <= 6; i++) {
                this.weekDays[i].date = moment(weekStart).add(i, 'days').format("YYYY-MM-DD")
            }

            this.mobileSelectedDay =  this.weekDays[currentDate.isoWeekday() - 1];

        },
        async mobileSetCalendar() {

            this.onMobile = true;
            
            await this.view('day')
            if (moment(this.focus).day()===0) {
                this.weekDays[6].date = moment(this.focus).toISOString().substr(0, 10)
                this.mobileSelectedDay = this.weekDays[6]
            } else {
                this.weekDays.map(item => {
                    return item.date = moment(this.focus).add(item.id + 2 - moment(this.focus).day(), 'days').toISOString().substr(0, 10)
                })
                this.mobileSelectedDay = this.weekDays[moment(this.focus).day() - 1]
            }
        },
        // new event
        removeFromNew(member) {
           
            const index = this.newAppointment.members.findIndex(item => item.id === member.id)
            if (index >= 0) this.newAppointment.members.splice(index, 1)
        },
        newAppointmentCourseSelected() {
            let selectedCourse = this.courses.findIndex(item => item.id===this.newAppointment.course_id)
           
            this.newAppointment.color = this.courses[selectedCourse].color
            this.newAppointment.duration = this.courses[selectedCourse].duration
            this.pickedColor = this.courses[selectedCourse].color
        },

        closeConfirmAddModal(){
            this.confirmAddModal = false;
            this.addEventLoading = false;
        },

        closeViewAppointmentModal(){
            this.viewAppointmentModal = false;
            this.dragEvent = null;
        },

        create(to_keep) {
            this.addEventLoading = true
            let appointment = Object.assign({}, this.newAppointment);
            appointment.members = appointment.members.map(item => item.id);
            appointment.color =  this.pickedColor;
            appointment.to_keep = to_keep;
            
            if (this.newAppointment.start_time) {
                appointment.start_time = this.newAppointment.start_time.HH + ':' + this.newAppointment.start_time.mm
            }
            axios.post('/backoffice/calendar/store', appointment)
                .then(response => {
                    this.addEventLoading = false;
                    
                    if(response.data.status==="OK") {
                        this.fetchCurrent()
                        this.newAppointmentModal = false
                        
                        this.confirmAddModal = false; 
                        EventBus.$emit('SHOW_NOTIFICATION',{
                                message: response.data.message,
                                color: 'success',
                                timeout: 5000
                            })
                    }else if(response.data.status==="CONFIRMATION"){
                            this.confirmAddModal = true;                     
                            }
                })
                .catch(error => {
                   this.addEventLoading = false;

                    EventBus.$emit('SHOW_NOTIFICATION',{
                            message: error.response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                    this.responseErrors = error.response.data.errors
                })
        },
        // filter calendar by trainer
        trainerSelected() {
            let from = this.$refs.calendar.lastStart.date
            let to = this.$refs.calendar.lastEnd.date
            this.fetchAppointments(from, to)
        },
        // click on event
        showEvent({ nativeEvent, event }) {
            if(this.preventShowEvent){
                this.preventShowEvent = false;
                return null;
            }
            const open = () => {
                // this.selectedEvent.trainer.full_name = ''
                this.eventLoading = true
                this.selectedEvent.id = event.id
                this.selectedElement = nativeEvent.target
                axios.get('/backoffice/calendar/appointment/'+this.selectedEvent.id)
                    .then(response => {
                        this.selectedEvent = response.data
                        this.selectedEvent.duration = moment(this.selectedEvent.end).diff(moment(this.selectedEvent.start), 'minutes')
                        this.editAppointment.name = response.data.name
                        this.editAppointment.start_date = response.data.start
                        this.editAppointment.start_time = moment(response.data.start).format('HH:mm')
                        this.editAppointment.duration = response.data.duration
                        this.editAppointment.trainer_id = response.data.trainer.id
                        this.editAppointment.id = response.data.id
                        this.editAppointment.color = response.data.color
                        this.editAppointment.days = response.data.days
                        this.editAppointment.end_date = response.data.recurring_until;
                        this.editAppointment.members = response.data.clients
                        this.editAppointment.course_id = response.data.course_id
                        this.editAppointment.is_online = response.data.is_online
                        this.editAppointment.session_url = response.data.session_url
                        this.editAppointment.location_id = response.data.location != null ? response.data.location.id : null;
                        this.pickedColor = response.data.color
                        this.editAppointment.recurring = response.data.recurring
                        this.selectedEvent.clients = response.data.clients
                        this.selectedEvent.clients.map(client => {
                            client.profile_photo += '?w=60'
                            return client
                        })
                        if (moment(response.data.start) > moment().add(1, 'hours')) {
                            this.selectedEvent.isFuture = true
                        } else {
                            this.selectedEvent.isFuture = false
                        }
                        this.eventLoading = false
                    })
                    .catch(error => {
                      
                    })
                setTimeout(() => {
                    this.viewAppointmentModal = true
                }, 10)
            }
            if (this.viewAppointmentModal) {
                this.viewAppointmentModal = false
                setTimeout(open, 10)
            } else {
               open()
            }
            nativeEvent.stopPropagation()
        },
        // fecth events by dates
        fetchAppointments(from, to) {

            this.form.from_date = from;
            this.form.to_date = to;
            
             this.$inertia.get("/backoffice/calendar", pickBy(this.form), {
                preserveState: true,
                preserveScroll: true,
                only: ['appointments'],
                onFinish: visit => {
                   
                }
             });
        },
        fetchCurrent() {
           this.fetchAppointments(this.$refs.calendar.lastStart.date, this.$refs.calendar.lastEnd.date)
        },
        // filters
        allowedMinutes: v => v % 15 === 0,
        formatEventTime(date) {
            return new Date(date).toLocaleTimeString('en-US', {
            hour: "2-digit", 
            minute: "2-digit", 
            hour12: false
            })
        },
        // click on day in calendar
        viewDay({ date }) {
            this.focus = date
            this.currentType = 'day'
            this.fetchAppointments(date, date)
        },
        // day|week|month nav buttons
        view(value) {
            let from = ''
            let to = ''
            let start = this.$refs.calendar.lastStart.date
            if (value==='month') {
                from = moment(start).format("YYYY-MM-01")
                to = moment(start).format("YYYY-MM-" + moment(start).daysInMonth())
            } else if (value==='week') {
                from = moment(start).startOf("week").format("YYYY-MM-DD")
                to = moment(start).endOf("week").format("YYYY-MM-DD")
            } else if (value==='day') {
                from = moment(start).format("YYYY-MM-DD")
                to = moment(start).format("YYYY-MM-DD")
            }
            this.currentType = value
            this.focus = start
            this.fetchAppointments(from, to)
        },
        // today nav button
        setToday(value) {
            let from = ''
            let to = ''
            if (value==='month') {
                from = moment().format("YYYY-MM-01")
                to = moment().format("YYYY-MM-" + moment().daysInMonth())
            } else if (value==='week') {
                from = moment().startOf("week").format("YYYY-MM-DD")
                to = moment().endOf("week").format("YYYY-MM-DD")
            } else if (value==='day') {
                from = moment().format("YYYY-MM-DD")
                to = moment().format("YYYY-MM-DD")
            }
            
            this.focus = ''
            
            this.currentType = value;
            this.fetchAppointments(from, to);
           
        },
        // next and prev nav buttons
        async prev() {
            await this.$refs.calendar.prev()
            let from = await this.$refs.calendar.lastStart.date
            let to = await this.$refs.calendar.lastEnd.date
            this.fetchAppointments(from, to)
        },
        async next() {
            await this.$refs.calendar.next()
            let from = await this.$refs.calendar.lastStart.date
            let to = await this.$refs.calendar.lastEnd.date
            this.fetchAppointments(from, to)
        },
        // event color
        picked(color) {
            this.pickedColor = color
            this.newAppointment.color = color
            this.responseErrors.color = ''
        },
        getEventColor(event) {
            return event.color
        },
    },
    filters:{
        formatIndex: function(value) {
            console.log(value)
                return value < 10 ? '0' + value : value
        },
        formatMobileDates: (value) => {
            return moment(value).format('DD.MM')
        }
    }
}

</script>